import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, finalize, Observable, of, tap, Subject } from 'rxjs';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})

export class ProfileModalService {
  // private _getAdminById="AdminLogin/get-admin-by-id";
  private _getAdminById="User/get-user-info";

  private profileImageUpdatedSubject = new BehaviorSubject<string>('');

  profileImageUpdated$ = this.profileImageUpdatedSubject.asObservable();

  constructor(private http: HttpClient, private _config: ConfigService) {}

  private isVisibleSubject = new BehaviorSubject<boolean>(false);
  isVisible$ = this.isVisibleSubject.asObservable();
  
  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();
  
  private errorSubject = new BehaviorSubject<string>('');
  error$ = this.errorSubject.asObservable();

  // Add profile data subject
  private profileDataSubject = new BehaviorSubject<any>(null);
  profileData$ = this.profileDataSubject.asObservable();

  // Add modal opened event subject
  private modalOpenedSubject = new Subject<void>();
  modalOpened$ = this.modalOpenedSubject.asObservable();

  // Getter for current visibility state
  get isOpen(): boolean {
    return this.isVisibleSubject.value;
  }

  // Add method to emit image update
  updateProfileImage(imageUrl: string) {
    this.profileImageUpdatedSubject.next(imageUrl);
  }

  open() {
    console.log('Modal service: Opening modal');
    this.isVisibleSubject.next(true);
    document.body.classList.add('modal-open');
    
    // Emit modal opened event to trigger data loading
    this.modalOpenedSubject.next();
  }

  close() {
    console.log('Modal service: Closing modal');
    this.isVisibleSubject.next(false);
    document.body.classList.remove('modal-open');
    
    // Clear any errors when closing
    this.errorSubject.next('');
  }

  // Method to fetch admin profile data
  fetchAdminProfile(adminId: string): Observable<any> {
    if (!adminId) {
      const error = 'Admin ID is required';
      this.errorSubject.next(error);
      return of(null);
    }

    this.loadingSubject.next(true);
    this.errorSubject.next(''); // Clear previous errors

    const url = `${this._config._url}${this._getAdminById}/${adminId}`;

    return this.http.get<any>(url).pipe(
      tap((data) => {
        console.log('Profile data fetched successfully:', data);
        this.profileDataSubject.next(data.data);
      }),
      catchError((error) => {
        console.error('Error fetching admin profile:', error);
        const errorMessage = this.extractErrorMessage(error);
        this.errorSubject.next(errorMessage);
        this.profileDataSubject.next(null);
        return of(null);
      }),
      finalize(() => {
        this.loadingSubject.next(false);
      })
    );
  }

  // Method to clear profile data (useful when switching users)
  clearProfileData() {
    this.profileDataSubject.next(null);
    this.errorSubject.next('');
  }

  // Helper method to extract error messages
  private extractErrorMessage(error: any): string {
    if (!error?.error) {
      return 'Failed to load profile data. Please try again.';
    }

    if (typeof error.error.message === 'string') {
      return error.error.message;
    }

    if (error.error.errors && typeof error.error.errors === 'object') {
      const validationMessages = Object.values(error.error.errors)
        .map(err => Array.isArray(err) ? err.join(', ') : err)
        .join('\n');
      return validationMessages || 'Validation error occurred.';
    }

    return error.message || 'An unexpected error occurred while loading profile data.';
  }
}